import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  Users, Gavel, Package, DollarSign, TrendingUp, TrendingDown,
  Calendar, MapPin, Radio, UserPlus, BarChart2,
  FileText, Settings, RefreshCw, ChevronRight,
  AlertCircle, WifiOff, ArrowRight, Clock,
  Activity, ChevronLeft, Minus,
} from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import dashboardService from '../../services/dashboardService'

// ─── Formatadores ────────────────────────────────────────────────────────────
function formatCurrency(value) {
  if (!value && value !== 0) return '—'
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}K`
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
}

function diasAte(iso) {
  if (!iso) return null
  return Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24))
}

// ─── Trend Badge ─────────────────────────────────────────────────────────────
function TrendBadge({ curr, prev, prevLabel, formatFn }) {
  if (prev == null || curr == null) return null
  const diff = curr - prev
  const pct  = prev === 0 ? (curr > 0 ? 100 : 0) : Math.round(Math.abs(diff / prev) * 100)
  const up   = diff > 0
  const flat = diff === 0

  if (flat) return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-gray-400">
      <Minus className="w-2.5 h-2.5" />
      igual ao {prevLabel}
    </span>
  )

  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold
      ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : '-'}{pct}%
      <span className="font-normal text-gray-400 ml-0.5">vs {prevLabel}</span>
    </span>
  )
}

// ─── Stat Card ───────────────────────────────────────────────────────────────
function StatCard({ name, shortName, value, sub, icon: Icon, accent, onClick, loading, curr, prev, prevLabel }) {
  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-5
        flex items-center gap-3 group relative overflow-hidden
        ${onClick ? 'cursor-pointer hover:shadow-md active:scale-[0.99] transition-all duration-150' : ''}
      `}
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:hidden font-semibold text-gray-400 uppercase tracking-wider leading-tight">
          {shortName ?? name}
        </p>
        <p className="hidden sm:block text-xs font-semibold text-gray-400 uppercase tracking-wider leading-tight">
          {name}
        </p>
        <p className={`mt-1 text-xl sm:text-3xl font-bold text-gray-900 leading-tight transition-opacity ${loading ? 'opacity-25' : 'opacity-100'}`}>
          {loading ? '···' : value}
        </p>
        <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 leading-tight">{sub}</p>
        {!loading && prev != null && (
          <div className="mt-1.5">
            <TrendBadge curr={curr} prev={prev} prevLabel={prevLabel} />
          </div>
        )}
      </div>
      <div className="flex-shrink-0 p-2 sm:p-3 rounded-xl" style={{ backgroundColor: `${accent}18` }}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: accent }} />
      </div>
    </div>
  )
}

// ─── Cotação Widget ───────────────────────────────────────────────────────────
function CotacaoWidget({ data, loading, error, orgEstado }) {
  const [estadoSelecionado, setEstadoSelecionado] = React.useState(orgEstado || 'MG')

  React.useEffect(() => {
    if (!data?.estados) return
    const found = data.estados.find((e) => e.uf === orgEstado)
    if (found) setEstadoSelecionado(orgEstado)
  }, [orgEstado, data])

  const estado = data?.estados?.find((e) => e.uf === estadoSelecionado)
  const outros = data?.estados?.filter((e) => e.uf !== estadoSelecionado).slice(0, 5) ?? []

  return (
    <div className="card-rematei flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-emerald-50 rounded-lg">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Cotação — Arroba do Boi</h3>
            {data && (
              <div className="flex items-center gap-1.5 mt-0.5">
                {data.realtime ? (
                  <>
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[11px] font-medium text-emerald-600">Tempo real</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3 text-amber-500" />
                    <span className="text-[11px] text-amber-600">Referência</span>
                  </>
                )}
                {data.data_atualizacao && (
                  <span className="text-[11px] text-gray-300 ml-1">
                    · {new Date(data.data_atualizacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-1 flex items-center justify-center py-10">
          <div className="flex flex-col items-center gap-2 text-gray-300">
            <Activity className="w-6 h-6 animate-pulse" />
            <span className="text-xs">Buscando cotação…</span>
          </div>
        </div>
      )}

      {/* Erro */}
      {error && !loading && (
        <div className="flex items-center gap-2 text-amber-700 text-sm bg-amber-50 border border-amber-100 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Não foi possível buscar a cotação agora.</span>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Seletor de estados */}
          <div className="flex flex-wrap gap-1 mb-4">
            {data.estados.map((e) => (
              <button
                key={e.uf}
                onClick={() => setEstadoSelecionado(e.uf)}
                className={`
                  px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-all duration-100
                  ${estadoSelecionado === e.uf
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                    : 'border-gray-200 text-gray-500 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50'
                  }
                  ${e.uf === orgEstado && estadoSelecionado !== e.uf
                    ? 'border-emerald-200 text-emerald-600'
                    : ''
                  }
                `}
              >
                {e.uf}
              </button>
            ))}
          </div>

          {/* Preço principal */}
          {estado && (
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100 rounded-2xl p-5 text-center mb-4">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">
                {estado.regiao_scot ?? estado.nome}
                {estado.uf === orgEstado && (
                  <span className="ml-1.5 text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full align-middle">
                    Sua região
                  </span>
                )}
              </p>
              <p className="text-4xl font-extrabold text-emerald-800 leading-none">
                R$ {estado.preco_arroba.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-emerald-600 mt-1.5">por arroba · {estado.fonte_referencia}</p>
            </div>
          )}

          {/* Outros estados */}
          <div className="space-y-0.5">
            {outros.map((e) => (
              <button
                key={e.uf}
                onClick={() => setEstadoSelecionado(e.uf)}
                className="flex items-center justify-between w-full py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <span className="text-xs text-gray-600 group-hover:text-gray-900 transition-colors">
                  <span className="font-semibold text-gray-700">{e.uf}</span>
                  {' '}— {e.nome}
                </span>
                <span className="text-xs font-bold text-gray-700 group-hover:text-emerald-700 transition-colors">
                  R$ {e.preco_arroba.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </button>
            ))}
          </div>

          {data.aviso && (
            <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3 leading-relaxed">
              {data.aviso}
            </p>
          )}
        </>
      )}
    </div>
  )
}

// ─── Próximos Leilões ─────────────────────────────────────────────────────────
function ProximosLeiloes({ leiloes, navigate, ultimoLeilao }) {
  if (!leiloes?.length && !ultimoLeilao) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Calendar className="w-8 h-8 text-gray-200 mb-2" />
        <p className="text-sm text-gray-400">Nenhum leilão agendado</p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {(leiloes ?? []).map((l) => {
        const dias = diasAte(l.data_leilao)
        return (
          <div
            key={l.id}
            onClick={() => navigate(`/leiloes/${l.id}`)}
            className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors group"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate leading-tight">{l.nome}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-gray-400 flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" />
                  {l.cidade}/{l.uf}
                </span>
                <span className="text-xs text-gray-400">{formatDate(l.data_leilao)}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {dias !== null && dias <= 7 && (
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${
                    dias === 0
                      ? 'bg-red-100 text-red-700'
                      : dias <= 2
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {dias === 0 ? 'Hoje' : `${dias}d`}
                </span>
              )}
              {l.link_transmissao && (
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center" title="Com transmissão">
                  <Radio className="w-2.5 h-2.5 text-green-600" />
                </div>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </div>
          </div>
        )
      })}

      {ultimoLeilao && (
        <div className="pt-3 mt-2 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-1.5">
            <Clock className="w-3 h-3 text-gray-300" />
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Último realizado</p>
          </div>
          <p className="text-xs font-semibold text-gray-600 truncate">{ultimoLeilao.nome}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatDate(ultimoLeilao.data_leilao)} · {ultimoLeilao.cidade}/{ultimoLeilao.uf}
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Ações Rápidas ────────────────────────────────────────────────────────────
const ACOES_SECUNDARIAS = [
  { label: 'Clientes', icon: Users, path: '/clientes' },
  { label: 'Cadastrar', icon: UserPlus, path: '/clientes' },
  { label: 'Leilões', icon: Calendar, path: '/leiloes' },
  { label: 'Leiloeiros', icon: Gavel, path: '/leiloeiros' },
  { label: 'Comissões', icon: BarChart2, path: '/comissoes' },
  { label: 'Relatórios', icon: FileText, path: '/mapas/mapa-geral' },
  { label: 'Config.', icon: Settings, path: '/configuracoes' },
]

function AcoesRapidas({ navigate }) {
  return (
    <div className="card-rematei flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-800">Ações Rápidas</h3>

      <button
        onClick={() => navigate('/leiloes/novo')}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 shadow-sm transition-all duration-150"
      >
        <Gavel className="w-4 h-4" />
        Novo Leilão
      </button>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2">
        {ACOES_SECUNDARIAS.map(({ label, icon: Icon, path }) => (
          <button
            key={label}
            onClick={() => navigate(path)}
            className="flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl border border-gray-100 text-xs font-medium text-gray-600
                       hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all duration-100"
          >
            <Icon className="w-4 h-4" />
            <span className="leading-tight text-center">{label}</span>
          </button>
        ))}
      </div>

      <button
        onClick={() => navigate('/leilao-tempo-real')}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:bg-orange-700 shadow-sm transition-all duration-150"
      >
        <Radio className="w-4 h-4" />
        Leilão em Tempo Real
      </button>
    </div>
  )
}

const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']

// ─── Dashboard principal ──────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate()
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const orgEstado = organizadorAtivo?.estado

  const hoje = new Date()
  const [mesSel, setMesSel] = React.useState(hoje.getMonth() + 1)
  const [anoSel, setAnoSel] = React.useState(hoje.getFullYear())
  const [todoPeriodo, setTodoPeriodo] = React.useState(false)

  const isMesAtual = !todoPeriodo && mesSel === hoje.getMonth() + 1 && anoSel === hoje.getFullYear()
  const [showPicker, setShowPicker] = React.useState(false)
  const [anoNav, setAnoNav] = React.useState(anoSel)
  const pickerRef = React.useRef(null)

  React.useEffect(() => {
    if (!showPicker) return
    const handler = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showPicker])

  const selecionarMes = (m) => {
    const futuro = anoNav > hoje.getFullYear() || (anoNav === hoje.getFullYear() && m > hoje.getMonth() + 1)
    if (futuro) return
    setMesSel(m); setAnoSel(anoNav); setTodoPeriodo(false); setShowPicker(false)
  }
  const selecionarTodoPeriodo = () => { setTodoPeriodo(true); setShowPicker(false) }
  const voltarMesAtual = () => { setMesSel(hoje.getMonth() + 1); setAnoSel(hoje.getFullYear()); setTodoPeriodo(false) }

  const { data: stats, isLoading: loadingStats, refetch: refetchStats } = useQuery(
    ['dashboard-stats', orgId, todoPeriodo ? 'all' : `${mesSel}-${anoSel}`],
    () => dashboardService.getStats(orgId, mesSel, anoSel, todoPeriodo),
    { enabled: !!orgId, staleTime: 60_000, retry: 1 }
  )

  const orgCidade = organizadorAtivo?.cidade
  const orgUF = organizadorAtivo?.estado
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'

  const { data: cotacao, isLoading: loadingCotacao, error: errorCotacao } = useQuery(
    ['cotacao-boi-gordo', orgCidade, orgUF],
    () => dashboardService.getCotacaoBoi(orgCidade, orgUF),
    { staleTime: 15 * 60 * 1000, retry: 1, enabled: !isFluxoCavalo }
  )

  const t   = stats?.totais
  const ant = stats?.anterior
  // backend já retorna em PT-BR ex: "Abril/2026" → abreviamos para "Abr/2026"
  const prevLabel = stats?.label_anterior
    ? stats.label_anterior.replace(/^(\w{3})\w+\/(\d{4})$/, '$1/$2')
    : null

  const trend = (key) => todoPeriodo ? {} : { curr: t?.[key], prev: ant?.[key] }

  const statCards = [
    {
      name: 'Clientes',
      shortName: 'Clientes',
      value: t ? (todoPeriodo ? t.clientes_total : t.clientes).toLocaleString('pt-BR') : '—',
      sub: t ? (todoPeriodo ? `${t.clientes_total.toLocaleString('pt-BR')} total` : `de ${t.clientes_total.toLocaleString('pt-BR')} total`) : '…',
      icon: Users, accent: '#3B82F6',
      onClick: () => navigate('/clientes'),
      ...trend('clientes'),
    },
    {
      name: 'Leilões',
      shortName: 'Leilões',
      value: t?.leiloes?.toLocaleString('pt-BR') ?? '—',
      sub: t ? `${t.leiloes_ativos} ativos agora` : '…',
      icon: Gavel, accent: '#10B981',
      onClick: () => navigate('/leiloes'),
      ...trend('leiloes'),
    },
    {
      name: 'Lotes',
      shortName: 'Lotes',
      value: t?.lotes?.toLocaleString('pt-BR') ?? '—',
      sub: t ? `${t.lotes_faturados ?? 0} faturados` : '…',
      icon: Package, accent: '#F97316',
      onClick: () => navigate('/lotes'),
      ...trend('lotes'),
    },
    {
      name: 'Vendas',
      shortName: 'Vendas',
      value: t ? formatCurrency(t.faturamento) : '—',
      sub: t ? `${(t.animais_faturados ?? 0).toLocaleString('pt-BR')} animais` : '…',
      icon: DollarSign, accent: '#059669',
      onClick: null,
      ...trend('faturamento'),
    },
    {
      name: 'Comissões',
      shortName: 'Comissões',
      value: t ? formatCurrency(t.comissoes ?? 0) : '—',
      sub: t ? 'comissão a receber' : '…',
      icon: TrendingUp, accent: '#7C3AED',
      onClick: () => navigate('/gestao/contas-receber'),
      ...trend('comissoes'),
    },
  ]

  return (
    <div className="space-y-5 sm:space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {organizadorAtivo?.nome_fantasia ?? organizadorAtivo?.razao_social ?? ''}
          </p>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Picker de mês */}
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => { setAnoNav(anoSel); setShowPicker(v => !v) }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all
                ${showPicker ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-gray-50'}
                ${!isMesAtual ? 'border-blue-200 text-blue-600' : ''}`}
            >
              <Calendar className="w-3.5 h-3.5" />
              {todoPeriodo ? 'Todo o período' : `${MESES_PT[mesSel - 1].slice(0, 3)}/${anoSel}`}
              <ChevronRight className={`w-3 h-3 transition-transform ${showPicker ? 'rotate-90' : ''}`} />
            </button>

            {showPicker && (
              <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56">
                {/* Seletor de ano */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setAnoNav(a => a - 1)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-bold text-gray-800">{anoNav}</span>
                  <button
                    onClick={() => setAnoNav(a => a + 1)}
                    disabled={anoNav >= hoje.getFullYear()}
                    className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Grid de meses */}
                {/* Todo o período */}
                <button
                  onClick={selecionarTodoPeriodo}
                  className={`w-full mb-2 py-1.5 rounded-lg text-xs font-medium transition-all border
                    ${todoPeriodo ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'}`}
                >
                  Todo o período
                </button>

                <div className="grid grid-cols-3 gap-1">
                  {MESES_PT.map((nome, i) => {
                    const m = i + 1
                    const futuro = anoNav > hoje.getFullYear() || (anoNav === hoje.getFullYear() && m > hoje.getMonth() + 1)
                    const ativo = !todoPeriodo && m === mesSel && anoNav === anoSel
                    return (
                      <button
                        key={m}
                        onClick={() => selecionarMes(m)}
                        disabled={futuro}
                        className={`py-1.5 rounded-lg text-xs font-medium transition-all
                          ${ativo ? 'bg-blue-600 text-white' : ''}
                          ${!ativo && !futuro ? 'text-gray-700 hover:bg-blue-50 hover:text-blue-700' : ''}
                          ${futuro ? 'text-gray-300 cursor-not-allowed' : ''}
                        `}
                      >
                        {nome.slice(0, 3)}
                      </button>
                    )
                  })}
                </div>

                {!isMesAtual && (
                  <button
                    onClick={() => { voltarMesAtual(); setShowPicker(false) }}
                    className="mt-2 w-full text-xs text-center text-blue-600 hover:text-blue-800 font-medium py-1.5 border-t border-gray-100"
                  >
                    Ir para o mês atual
                  </button>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => refetchStats()}
            disabled={loadingStats}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900
                       px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50
                       disabled:opacity-50 transition-all duration-150"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingStats ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {statCards.map((card) => (
          <StatCard key={card.name} {...card} loading={loadingStats} prevLabel={prevLabel} />
        ))}
      </div>

      {/* ── Linha principal ── */}
      <div className={`grid grid-cols-1 ${isFluxoCavalo ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-4 sm:gap-5`}>

        {/* Ações rápidas — aparece por último no mobile */}
        <div className="order-3 lg:order-1">
          <AcoesRapidas navigate={navigate} />
        </div>

        {/* Cotação — destaque no centro (não se aplica ao fluxo cavalo) */}
        {!isFluxoCavalo && (
          <div className="order-1 lg:order-2">
            <CotacaoWidget
              data={cotacao}
              loading={loadingCotacao}
              error={errorCotacao}
              orgEstado={orgEstado}
            />
          </div>
        )}

        {/* Próximos Leilões */}
        <div className="order-2 lg:order-3">
          <div className="card-rematei h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">Próximos Leilões</h3>
              <button
                onClick={() => navigate('/leiloes')}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              >
                Ver todos
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <ProximosLeiloes
              leiloes={stats?.proximos_leiloes}
              navigate={navigate}
              ultimoLeilao={stats?.ultimo_leilao}
            />
          </div>
        </div>

      </div>
    </div>
  )
}
